import { useMemo, useState } from 'react';

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { Table } from 'src/components/Table';
import Input from 'src/components/FormComponents/Input';
import {
    DocumentsIcon,
    PlusIcon,
    SearchIcon,
} from 'src/components/Icons/icons';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { ScrollableModal } from 'src/components/Modals/ScrollableModal';
import { formatPhoneNumber } from 'src/utils/stringUtils';
import { employeeDepartmentPath } from 'src/utils/employeeDepartment';
import { useCreator, useCreators } from 'src/view/hooks/creator';
import { useAvatarFetch } from 'src/view/hooks/useImageFetch';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import {
    OrganizationRolePermission,
    OrganizationStructureVersion,
} from 'src/domain/organization';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import { LoginStatusIndicator } from 'src/components/LoginStatusIndicator';
import { matchesAccountStatus } from 'src/utils/accountStatusFilter';
import { AccountStatusFilterTabs } from 'src/components/AccountStatusFilterTabs';
import { useAccountStatusFilter } from 'src/view/hooks/useAccountStatusFilter';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { isOrganizationAdministrator } from 'src/domain/user';

import { CreatorProfileEdit } from './Profile/CreatorProfileEdit';
import { ReplaceLegacyAdministratorModal } from './ReplaceLegacyAdministratorModal';

type CreatorListItem = {
    id: string;
    profile: {
        firstName: string;
        lastName: string;
        avatar?: string;
    };
    email?: string;
    phoneNumber?: string;
    roleIds?: string[];
    departmentGroupId?: string | null;
    organizationAdmin?: boolean;
    lastLoggedInAt?: string;
    hasEverLoggedIn?: boolean;
    originalOrganizationAccount?: boolean;
    avatarUrl?: string;
};

const CreatorEditModal = ({
    creatorId,
    onClose,
}: {
    creatorId: string;
    onClose: () => void;
}) => {
    const { data: creator } = useCreator(creatorId);

    // Opens once the employee is loaded, so it never shows a half-empty box first.
    return (
        <ScrollableModal visible={Boolean(creator)} onClose={onClose}>
            {creator && (
                <CreatorProfileEdit creator={creator} onDelete={onClose} />
            )}
        </ScrollableModal>
    );
};

export const Creators = () => {
    const { creators, isLoading } = useCreators();
    const getAvatar = useAvatarFetch();
    const { t } = useTranslation();
    const { organization } = useCurrentOrganization();
    const user = useSignedInUser();
    const [searchValue, setSearchValue] = useState<string>('');
    const [editingCreatorId, setEditingCreatorId] = useState<string | null>(
        null,
    );
    const [viewingLegacyAdministrator, setViewingLegacyAdministrator] =
        useState(false);
    const hasWritePermission = useCreatorRolePermission(
        OrganizationRolePermission.writeEmployees,
    );
    const canCreate = !user?.supportMode && hasWritePermission;
    const canManageAdministrators = isOrganizationAdministrator(
        user,
        organization?.id,
    );
    const canFilterEmployees =
        Boolean(user?.supportMode) ||
        hasWritePermission ||
        canManageAdministrators;
    const { statusFilter, setStatusFilter } =
        useAccountStatusFilter(canFilterEmployees);
    const canOpenLegacyAdministrator = Boolean(
        organization &&
            organization.structureVersion !==
                OrganizationStructureVersion.separated &&
            !organization.legacyAdministratorRetiredAt &&
            canManageAdministrators &&
            !user?.supportMode,
    );
    const canDeleteLegacyAdministrator = Boolean(
        canOpenLegacyAdministrator && user?.id !== organization?.id,
    );
    const canReadEmployees = useCreatorRolePermission(
        OrganizationRolePermission.readEmployees,
    );
    const resolvedCreators = useMemo<CreatorListItem[]>(() => {
        if (!canReadEmployees) return [];

        const originalOrganizationAccount: CreatorListItem[] =
            organization &&
            organization.structureVersion !==
                OrganizationStructureVersion.separated &&
            !organization.legacyAdministratorRetiredAt
                ? [
                      {
                          id: organization.id,
                          profile: {
                              firstName: organization.profile.firstName,
                              lastName: organization.profile.lastName,
                              avatar: organization.superuserAvatar,
                          },
                          email: organization.email,
                          phoneNumber: organization.phoneNumber,
                          organizationAdmin: true,
                          // The original administrator is the organization's own login and
                          // has no tracked login history, so it always counts as active.
                          hasEverLoggedIn: true,
                          originalOrganizationAccount: true,
                          avatarUrl: organization.superuserAvatarUrl,
                      },
                  ]
                : [];

        return [...originalOrganizationAccount, ...creators];
    }, [canReadEmployees, creators, organization]);
    const showActions = canCreate || canOpenLegacyAdministrator;
    const emptyMessage = canReadEmployees
        ? t('modules.creators.empty')
        : t('creators.list.missingReadEmployeesPermission');

    const roleNameById = useMemo(() => {
        const entries = organization?.roles?.map(
            orgRole => [orgRole.id, orgRole.name || orgRole.id] as const,
        );
        return new Map(entries ?? []);
    }, [organization?.roles]);

    // Deleted groups are missing from the map and show as no department.
    const departmentPathById = useMemo(() => {
        const groups = organization?.groups ?? [];
        return new Map(
            groups.map(
                group =>
                    [
                        group.id,
                        employeeDepartmentPath(group.id, groups),
                    ] as const,
            ),
        );
    }, [organization?.groups]);

    const filteredCreators = useMemo(() => {
        const query = searchValue.trim().toLowerCase();
        const statusFilteredCreators = resolvedCreators.filter(creator =>
            matchesAccountStatus(creator, statusFilter),
        );
        if (!query) return statusFilteredCreators;

        return statusFilteredCreators.filter(
            ({
                profile,
                phoneNumber,
                roleIds,
                departmentGroupId,
                organizationAdmin,
            }) => {
                const fullName = `${profile.firstName ?? ''} ${
                    profile.lastName ?? ''
                }`.trim();
                const roleNames =
                    roleIds
                        ?.map(roleId => roleNameById.get(roleId))
                        .filter(Boolean)
                        .join(', ') ?? '';
                const departmentName = departmentGroupId
                    ? departmentPathById.get(departmentGroupId) ?? ''
                    : '';
                const formattedPhoneNumber = phoneNumber
                    ? formatPhoneNumber(phoneNumber)
                    : '';

                const adminRole = organizationAdmin
                    ? t('roles.systemAdmin')
                    : '';
                const haystack =
                    `${fullName} ${adminRole} ${roleNames} ${departmentName} ${
                        phoneNumber ?? ''
                    } ${formattedPhoneNumber}`
                        .trim()
                        .toLowerCase();

                return haystack.includes(query);
            },
        );
    }, [
        departmentPathById,
        resolvedCreators,
        roleNameById,
        searchValue,
        statusFilter,
        t,
    ]);

    return (
        <div className="h-full w-full relative text-woodyBrown">
            {editingCreatorId && (
                <CreatorEditModal
                    creatorId={editingCreatorId}
                    onClose={() => setEditingCreatorId(null)}
                />
            )}
            {viewingLegacyAdministrator && organization && (
                <ReplaceLegacyAdministratorModal
                    organizationId={organization.id}
                    originalAdministrator={{
                        profile: organization.profile,
                        email: organization.email,
                        phoneNumber: organization.phoneNumber,
                        avatarUrl: organization.superuserAvatarUrl,
                    }}
                    canDelete={canDeleteLegacyAdministrator}
                    onClose={() => setViewingLegacyAdministrator(false)}
                />
            )}
            <div className="mb-4 flex flex-wrap justify-between items-center gap-3">
                {canFilterEmployees && (
                    <AccountStatusFilterTabs
                        value={statusFilter}
                        onChange={setStatusFilter}
                    />
                )}
                {resolvedCreators.length > 10 ? (
                    <Input
                        className="text-input w-full"
                        containerClassName="w-full max-w-xs"
                        placeholder={t('common.search')}
                        leadingIcon={SearchIcon}
                        value={searchValue}
                        onChange={event => setSearchValue(event.target.value)}
                    />
                ) : (
                    <div />
                )}

                {canCreate && (
                    <Link
                        to="/app/creators/new"
                        className="primary-btn-small ml-auto flex items-center gap-1 pl-2 pr-4 py-1"
                    >
                        <TextIcon SVG={PlusIcon} />
                        <span>{t('modules.creators.navNew')}</span>
                    </Link>
                )}
            </div>
            <Table
                columns={[
                    // Fixed shares keep columns near the old email layout,
                    // since department names are much shorter than emails.
                    { header: t('common.name'), className: 'w-[28%]' },
                    {
                        header: t('creators.list.roleNames'),
                        className: 'w-[24%]',
                    },
                    ...(isMobile
                        ? []
                        : [
                              {
                                  header: t('creators.list.department'),
                                  className: 'w-[26%]',
                              },
                          ]),
                    {
                        header: t('common.phone'),
                        className: 'min-w-[8rem] whitespace-nowrap',
                    },
                    ...(showActions
                        ? [
                              {
                                  header: '',
                                  className: 'w-0 text-right',
                              },
                          ]
                        : []),
                ]}
                data={filteredCreators}
                renderRow={({
                    id,
                    profile,
                    phoneNumber,
                    roleIds,
                    departmentGroupId,
                    organizationAdmin,
                    lastLoggedInAt,
                    hasEverLoggedIn,
                    originalOrganizationAccount,
                    avatarUrl,
                }) => {
                    const canEditCreator = Boolean(
                        canCreate &&
                            !originalOrganizationAccount &&
                            (!organizationAdmin || canManageAdministrators),
                    );
                    const canOpenOriginalAdministrator = Boolean(
                        originalOrganizationAccount &&
                            canOpenLegacyAdministrator,
                    );
                    const canOpenCreator =
                        canEditCreator || canOpenOriginalAdministrator;
                    const openCreator = () => {
                        if (canEditCreator) {
                            setEditingCreatorId(id);
                        } else if (canOpenOriginalAdministrator) {
                            setViewingLegacyAdministrator(true);
                        }
                    };
                    const roleNames = [
                        ...(organizationAdmin ? [t('roles.systemAdmin')] : []),
                        ...(roleIds
                            ?.map(roleId => roleNameById.get(roleId))
                            .filter((name): name is string => Boolean(name)) ??
                            []),
                    ];
                    const departmentName = departmentGroupId
                        ? departmentPathById.get(departmentGroupId)
                        : undefined;
                    const avatarProps = avatarUrl
                        ? { src: avatarUrl }
                        : getAvatar(profile.avatar);

                    return (
                        <tr key={id}>
                            <td className="px-4 py-3">
                                <div
                                    className={`group flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-3 ${
                                        canOpenCreator ? 'cursor-pointer' : ''
                                    }`}
                                    role={canOpenCreator ? 'button' : undefined}
                                    tabIndex={canOpenCreator ? 0 : undefined}
                                    onClick={openCreator}
                                    onKeyDown={event => {
                                        if (!canOpenCreator) return;
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault();
                                            openCreator();
                                        }
                                    }}
                                >
                                    <span className="relative flex h-10 w-10 flex-shrink-0">
                                        <img
                                            className="h-10 w-10 rounded-full object-cover"
                                            alt={`${profile.firstName} ${profile.lastName}`}
                                            {...avatarProps}
                                        />
                                        <LoginStatusIndicator
                                            className="absolute bottom-0 right-0 ring-2 ring-vanillaWhite"
                                            hasEverLoggedIn={hasEverLoggedIn}
                                            lastLoggedInAt={lastLoggedInAt}
                                            size="avatar"
                                        />
                                    </span>
                                    <span
                                        className={`font-semibold whitespace-nowrap ${
                                            canOpenCreator
                                                ? 'group-hover:underline'
                                                : ''
                                        }`}
                                    >
                                        {`${profile.firstName} ${profile.lastName}`}
                                    </span>
                                </div>
                            </td>
                            <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                {roleNames.length ? roleNames.join(', ') : '-'}
                            </td>
                            {!isMobile && (
                                <td className="px-4 py-3 content-center text-sm text-mongooseGray">
                                    {departmentName || '-'}
                                </td>
                            )}
                            <td className="px-4 py-3 content-center text-sm text-mongooseGray min-w-[8rem] whitespace-nowrap">
                                {phoneNumber
                                    ? formatPhoneNumber(phoneNumber)
                                    : '-'}
                            </td>
                            {showActions && (
                                <td className="px-4 py-3 content-center text-right">
                                    {canOpenCreator && (
                                        <button
                                            type="button"
                                            className="primary-btn-small p-1.5"
                                            onClick={openCreator}
                                        >
                                            <div className="relative w-4 h-4">
                                                <DocumentsIcon />
                                            </div>
                                        </button>
                                    )}
                                </td>
                            )}
                        </tr>
                    );
                }}
                emptyMessage={emptyMessage}
                isLoading={isLoading}
                loadingRowCount={3}
            />
        </div>
    );
};
